/**
 * "Bundle is in beta" for the browser (logic: packages/core logic/beta.ts): keeps the banner's per-device state in
 * localStorage, like the "Get the app" snooze, and says whether it wants the top slot.
 */
import { Beta, type BetaState } from "@bundle/core";
import { useState, useSyncExternalStore } from "react";
import { systemClock } from "./clock.ts";
import { APP_VERSION } from "./env.ts";
import { createStorage } from "./storage.ts";

const KEY = "beta.banner";
const storage = createStorage();

let state: BetaState = Beta.parseState(storage.get(KEY));
const listeners = new Set<() => void>();

function set(next: BetaState): void {
  state = next;
  storage.set(KEY, Beta.serializeState(next));
  for (const l of listeners) l();
}

/** × on the banner. */
export function dismissBeta(now: number = Date.now()): void {
  set(Beta.afterDismiss({ state, appVersion: APP_VERSION, nowMs: now }));
}

/** Feedback went through (FeedbackViewModel succeeded), from the banner or the Help menu. */
export function markBetaFeedbackSent(now: number = Date.now()): void {
  set(Beta.afterFeedback({ state, appVersion: APP_VERSION, nowMs: now }));
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Whether the beta banner wants to show on this device. */
export function useBetaBannerWanted(): boolean {
  const s = useSyncExternalStore(subscribe, () => state, () => state);
  // Decided once per session (the banner doesn't appear mid-session as time passes).
  const [nowMs] = useState(() => systemClock.now());
  return Beta.showBetaBanner({ state: s, appVersion: APP_VERSION, nowMs });
}

/** Test hook. */
export function resetBetaForTests(next: BetaState = {}): void {
  state = next;
  storage.remove(KEY);
}
